/**
 * What a CTS (Critical Timing Scan) run actually produced, as journal text.
 *
 * <p>Two questions the arrows alone cannot answer:
 *
 * <ol>
 *   <li><b>Did the marks land on turns?</b> An arrow is drawn at every qualifying day, whether or
 *       not price turned there. The share sitting on a genuine pivot is the study's hit rate, and
 *       without it the chart looks equally convincing whatever the orb.</li>
 *   <li><b>How far did price go afterwards?</b> A mark on a high that preceded a three-bar dip is a
 *       different claim from one that preceded a twenty-bar decline, and only the distribution
 *       separates a study worth running from one that marks noise.</li>
 * </ol>
 *
 * <p>Pure functions over bar data and the arrows, so both readings can be tested against a
 * hand-built series rather than against whatever the sky happened to do.
 */

/** How many bars either side a bar must dominate to count as a pivot for the hit rate. */
export const PIVOT_STRICTNESS_BARS = 3;

/** Bucket edges, in bars, for how long the move after a mark ran. */
export const MOVE_BUCKETS = [
  { from: 0, to: 3 },
  { from: 3, to: 6 },
  { from: 6, to: 9 },
  { from: 9, to: 12 },
  { from: 12, to: Infinity },
];

/** How far forward to look for the end of a move before giving up. */
export const MOVE_HORIZON_BARS = 60;

/**
 * Is this bar the strict extreme of the window `n` either side?
 *
 * <p>Strict, and over the FULL window: "the lowest low of the eleven bars centred here" is a claim
 * a reader can check by eye. A bar merely lower than its two neighbours is not a pivot, it is a
 * wiggle, and counting those would inflate the hit rate to near 100% and say nothing.
 */
export function isPivotAt(ohlc, index, direction, n = PIVOT_STRICTNESS_BARS) {
  const bar = ohlc[index];
  if (!bar) return false;
  const col = direction === 'up' ? 2 : 3;   // [open, close, low, high]
  const self = bar[col];
  if (typeof self !== 'number') return false;

  const from = index - n;
  const to = index + n;
  // Refused at the edges rather than measured over a half window: a bar three from the start would
  // be compared against fewer neighbours than one in the middle and would qualify more easily.
  if (from < 0 || to >= ohlc.length) return false;

  for (let i = from; i <= to; i++) {
    if (i === index) continue;
    const other = ohlc[i] && ohlc[i][col];
    if (typeof other !== 'number') continue;
    if (direction === 'up' ? other <= self : other >= self) return false;
  }
  return true;
}

/**
 * How many bars the move after a mark ran before it ended.
 *
 * <p>The move ends when price closes back through the level the mark was made at — above the high
 * for a down arrow, below the low for an up arrow. The length reported is the distance to the
 * EXTREME reached in between, not to the bar the move failed on: the question is how far the move
 * carried, and the failure bar is where it stopped carrying.
 *
 * @returns {number|null} bars to the extreme, or null when the move never ended inside the horizon
 *          (which is not the same as a long move, and must not be counted as one)
 */
export function moveLengthAfter(ohlc, index, direction, horizon = MOVE_HORIZON_BARS) {
  const start = ohlc[index];
  if (!start) return null;

  const down = direction === 'down';
  const level = down ? start[3] : start[2];        // the high, or the low
  if (typeof level !== 'number') return null;

  let extremeIdx = index;
  let extreme = down ? start[2] : start[3];

  const last = Math.min(ohlc.length - 1, index + horizon);
  for (let i = index + 1; i <= last; i++) {
    const bar = ohlc[i];
    if (!bar) continue;
    const close = bar[1];

    const running = down ? bar[2] : bar[3];
    if (typeof running === 'number') {
      if (down ? running < extreme : running > extreme) { extreme = running; extremeIdx = i; }
    }

    // The move is over once price closes back through where it started.
    if (typeof close === 'number' && (down ? close > level : close < level)) {
      return extremeIdx - index;
    }
  }
  return null;
}

/** Which bucket a bar count falls in, as `from-to`. Lower bound inclusive, upper exclusive. */
export function bucketFor(bars) {
  const b = MOVE_BUCKETS.find(x => bars >= x.from && bars < x.to);
  if (!b) return null;
  return b.to === Infinity ? `${b.from}+` : `${b.from}-${b.to}`;
}

/**
 * The statistics behind a run: pivot hit rate, and how far the moves after each mark carried.
 *
 * @param {Array<{date:string, position:string}>} points the arrows that were drawn
 * @param {{dates:string[], ohlc:Array<number[]>}} barData
 */
export function summariseGannRun(points, barData) {
  const dates = (barData && barData.dates) || [];
  const ohlc = (barData && barData.ohlc) || [];
  const list = Array.isArray(points) ? points : [];

  const indexOf = new Map(dates.map((d, i) => [d, i]));

  let onPivot = 0;
  let measurable = 0;
  let offData = 0;
  const buckets = {};
  const byDirection = { up: { total: 0, onPivot: 0, buckets: {} },
                        down: { total: 0, onPivot: 0, buckets: {} } };

  list.forEach((p) => {
    const i = indexOf.get(p.date);
    if (i === undefined) {
      // A mark on the padded forward year: the chart draws those bars, but no price exists there,
      // so there is nothing to score. Counted rather than skipped, because otherwise the
      // denominator quietly shrinks and a rate of "73 of 388" is reported against 400 arrows with
      // no explanation of where the other twelve went.
      offData += 1;
      return;
    }
    const direction = p.position === 'above' ? 'down' : 'up';
    const side = byDirection[direction];
    side.total += 1;

    if (isPivotAt(ohlc, i, direction)) { onPivot += 1; side.onPivot += 1; }

    const len = moveLengthAfter(ohlc, i, direction);
    // Null means the move had not ended inside the horizon, which is not the same as a long move.
    // Counted separately rather than folded into the top bucket, which would report a mark whose
    // outcome is unknown as the study's best result.
    if (len === null) return;
    measurable += 1;
    const b = bucketFor(len);
    if (!b) return;
    buckets[b] = (buckets[b] || 0) + 1;
    side.buckets[b] = (side.buckets[b] || 0) + 1;
  });

  const total = byDirection.up.total + byDirection.down.total;
  return {
    total,
    onPivot,
    pivotRate: total > 0 ? onPivot / total : 0,
    measurable,
    unresolved: total - measurable,
    offData,
    buckets,
    byDirection,
  };
}

/** One bucket line: `   3-6 bars   :  55  (22%)`. */
function bucketLines(buckets, measurable) {
  return MOVE_BUCKETS.map((b) => {
    const key = b.to === Infinity ? `${b.from}+` : `${b.from}-${b.to}`;
    const n = buckets[key] || 0;
    const pct = measurable > 0 ? Math.round((100 * n) / measurable) : 0;
    return `    ${(key + ' bars').padEnd(11)}: ${String(n).padStart(4)}  (${pct}%)`;
  });
}

/**
 * The journal entry for a run, as lines.
 *
 * <p>Written as text rather than as a table because the journal is a running log the reader scrolls
 * back through — the point is to be able to compare two runs at different orbs by eye, which needs
 * the parameters and the outcome in the same block.
 */
export function gannJournalLines({ symbol, scan, built, orb, maxCombinedOrb, stats }) {
  const pct = (n, d) => (d > 0 ? `${Math.round((100 * n) / d)}%` : '0%');

  const lines = [
    `CTS — ${symbol || '?'}`,
    `  orb ${orb}°, combined ≤ ${maxCombinedOrb}°`,
    `  ${scan.startDate} .. ${scan.endDate}: ${fmt(scan.daysScanned)} days scanned,`
      + ` ${fmt(scan.daysQualifying)} qualifying, ${fmt((scan.days || []).length)} within the ceiling`,
    `  ${fmt(built.points.length)} arrows drawn — ${fmt(built.up)} up, ${fmt(built.down)} down`,
  ];

  if (built.suppressed > 0) {
    lines.push(`  ${fmt(built.suppressed)} adjacent marks dropped, keeping the tighter of each run`);
  }
  if (built.truncated > 0) lines.push(`  ${fmt(built.truncated)} weaker not drawn`);
  if (built.offChart > 0) lines.push(`  ${fmt(built.offChart)} fell outside the chart`);

  if (stats.offData > 0) {
    // The forward year is drawn but has no price, so those marks cannot be scored either way.
    // Named here so the pivot rate below is read against the right denominator.
    lines.push(`  ${fmt(stats.offData)} marks sit past the last bar and cannot be scored yet`);
  }

  lines.push('');
  lines.push(`  On a ${PIVOT_STRICTNESS_BARS}-bar pivot: ${fmt(stats.onPivot)} of ${fmt(stats.total)}`
    + ` (${pct(stats.onPivot, stats.total)})`);
  lines.push(`    up arrows  : ${fmt(stats.byDirection.up.onPivot)} of ${fmt(stats.byDirection.up.total)}`
    + ` (${pct(stats.byDirection.up.onPivot, stats.byDirection.up.total)})`);
  lines.push(`    down arrows: ${fmt(stats.byDirection.down.onPivot)} of ${fmt(stats.byDirection.down.total)}`
    + ` (${pct(stats.byDirection.down.onPivot, stats.byDirection.down.total)})`);

  lines.push('');
  lines.push(`  Move after a DOWN arrow, bars to the low (${fmt(stats.byDirection.down.total)} marks):`);
  lines.push(...bucketLines(stats.byDirection.down.buckets,
    Object.values(stats.byDirection.down.buckets).reduce((a, b) => a + b, 0)));

  lines.push(`  Move after an UP arrow, bars to the high (${fmt(stats.byDirection.up.total)} marks):`);
  lines.push(...bucketLines(stats.byDirection.up.buckets,
    Object.values(stats.byDirection.up.buckets).reduce((a, b) => a + b, 0)));

  if (stats.unresolved > 0) {
    // Stated rather than folded into the last bucket: a move still running at the horizon has an
    // unknown length, and reporting it as the longest would flatter the study.
    lines.push('');
    lines.push(`  ${fmt(stats.unresolved)} marks had no resolved move within ${MOVE_HORIZON_BARS} bars`
      + ' (still running, or at the end of the chart) and are excluded from the buckets above.');
  }

  return lines;
}

function fmt(n) {
  return typeof n === 'number' ? n.toLocaleString() : String(n ?? 0);
}
